import { getNumberAbbreviation } from '@common/core-layer/utils'
import { formatCurrencyAmount } from '@common/core-layer/utils/currency'

import { courseCardTagMessageKey } from '@courses/library-layer/utils/course-card-tags.utils'

import { DISCOVER_CATEGORY_OPTIONS } from '../constants/discover.constants'
import type { DiscoverListing, DiscoverListingChip, DiscoverPriceLabel } from '../types/discover'

/**
 * Chips under a listing title.
 *
 * Only what the API actually provides gets a chip: an *absent* attribute is skipped entirely
 * (a placeholder chip reads as real metadata and misleads), whereas a value that is present but
 * unrecognised still renders from its API label (or `Other`), because the data does exist.
 *
 * Language and difficulty chips share the course card's locale keys (`library.myCourseCard.tags.*`),
 * which also map English-name values such as `italian`; the chip falls back to the API label when
 * the active locale has no such key.
 *
 * A bundle with no product tags collapses to a single `Bundle` chip. A one-product bundle that
 * carries that product's tags shows those tags instead.
 */
export function resolveDiscoverListingChips(listing: DiscoverListing): DiscoverListingChip[] {
  const chips: DiscoverListingChip[] = []

  if (listing.membersCount !== undefined) {
    chips.push({
      id: `${listing.id}-members`,
      kind: 'members',
      labelKey: 'discover.chips.members',
      params: { count: getNumberAbbreviation(listing.membersCount) },
    })
  }

  const pushCourseTagAttribute = (kind: 'language' | 'difficulty', value: string | undefined, label: string | undefined) => {
    if (value === undefined) return
    const labelKey = courseCardTagMessageKey({ category: kind, value, label: label ?? '' }) ?? undefined
    chips.push({ id: `${listing.id}-${kind}`, kind, labelKey, label })
  }

  pushCourseTagAttribute('language', listing.language, listing.languageLabel)
  pushCourseTagAttribute('difficulty', listing.difficulty, listing.difficultyLabel)

  if (listing.category !== undefined) {
    const known = DISCOVER_CATEGORY_OPTIONS.find(option => option.value === listing.category)
    chips.push({
      id: `${listing.id}-category`,
      kind: 'category',
      labelKey: known?.labelKey,
      label: known ? undefined : listing.categoryLabel,
    })
  }

  if (listing.isBundle && chips.length === 0) {
    return [{ id: `${listing.id}-bundle`, kind: 'bundle', labelKey: 'discover.chips.bundle' }]
  }

  return chips
}

/**
 * i18n key + params for the price line (`Free`, `$ 120.00`, `$ 200.00/month`).
 *
 * Returns a key rather than a string so the caller owns the `t()` call and stays reactive to
 * locale. The amount is formatted via `formatCurrencyAmount` — the APIs send an ISO code, not a
 * symbol, and the code also selects the grouping locale, so concatenating a symbol would both
 * guess the symbol and mis-group the digits.
 */
export function resolveDiscoverPriceLabel(listing: DiscoverListing): DiscoverPriceLabel {
  const { model, amount, currency, currencySymbol, interval } = listing.price

  if (model === 'free') {
    return { key: 'discover.price.free', params: {} }
  }

  const price = formatCurrencyAmount(amount, currency, 2, currencySymbol)

  if (model === 'recurring') {
    switch (interval) {
      case 'week':
        return { key: 'discover.price.perWeek', params: { price } }
      case 'year':
        return { key: 'discover.price.perYear', params: { price } }
      case 'month':
        return { key: 'discover.price.perMonth', params: { price } }
      default:
        // Recurring on an interval we could not map: show the bare amount rather than invent a cadence.
        return { key: 'discover.price.oneTime', params: { price } }
    }
  }

  return { key: 'discover.price.oneTime', params: { price } }
}

/**
 * Badge for a Discover tab. Both feeds load page by page and neither has an exact total (the Courses
 * tab merges and de-duplicates two feeds), so a partial count is marked with `+` and never reads as exact.
 */
export function formatDiscoverTabCount(loadedCount: number, hasMore: boolean): string {
  return hasMore ? `${loadedCount}+` : String(loadedCount)
}

/**
 * Reads a single Discover URL param as a string.
 *
 * Vue Router hands back `string[]` when a key repeats (`?q=a&q=b`) and `null` for a valueless key
 * (`?q`), so a plain `as string` cast lets `.trim()` throw mid-render. Only the first value is
 * read; anything that is not a string reads as empty.
 */
export function readDiscoverQueryParam(value: unknown): string {
  const first = Array.isArray(value) ? value[0] : value
  return typeof first === 'string' ? first : ''
}

/** True for `/discover` and anything under it — the routes the Discover search context owns. */
export function isDiscoverPath(path: string | null | undefined): boolean {
  return path === '/discover' || (path ?? '').startsWith('/discover/')
}
